import React from 'react';
import { Loader2 } from 'lucide-react';
import { buildStateHubUrl, stateSlugToDisplay } from '../../lib/stateCore.mjs';
import { useRouteStateSlug } from '../../hooks/useRouteStateSlug';
import { Button } from '../../src/components/ui/button';

interface EmptyStateProps {
    type: 'no-suburb' | 'searching' | 'no-results' | 'no-directory';
    suburb?: string;
    radius?: number;
    useRadius?: boolean;
    onActionClick: () => void;
    /** Pulse the CTA (platform's "waiting" idiom) — used when filters are
        active but the required location is missing, i.e. waiting on the user. */
    pulseAction?: boolean;
}

const EmptyState: React.FC<EmptyStateProps> = ({ type, suburb, radius, useRadius, onActionClick, pulseAction = false }) => {
    // ITEM 6 SLICE 1 (operator-ruled 2026-08-12) — the escape hatch was hardcoded to the
    // DEFAULT state, so a Victorian who found nothing was offered "Browse NSW suburbs".
    // It is the way OUT of a dead end, which makes it the worst possible place to send
    // someone to the wrong state. Derived from the route now, exactly like every other
    // state-dependent value on this page (UI-01/UI-16 class: never a defaulted state).
    const routeStateSlug = useRouteStateSlug();
    switch (type) {
        case 'no-suburb':
            return (
                <div className="col-span-full flex flex-col items-center justify-center h-full text-center px-6 py-10">
                    <h3 className="text-xl font-semibold text-text-primary mb-2">Start Your Search</h3>
                    <p className="text-text-muted mb-3 max-w-xs leading-relaxed text-sm">
                        Please select a location to view martial arts gyms in your area.
                    </p>
                    {/* UI-13: converted to <Button> (primary variant supplies the same
                        bg/text/hover colours); className preserves the pre-existing
                        px-6 py-3 / rounded-lg / text-base / font-semibold rendering and
                        border-0 cancels the variant's 1px transparent border so the box
                        size is unchanged. Only accepted delta: harmonised focus ring. */}
                    <Button
                        onClick={onActionClick}
                        className={`border-0 px-6 py-3 rounded-lg text-base font-semibold ${pulseAction ? 'animate-pulse' : ''}`}
                    >
                        Select Location
                    </Button>
                </div>
            );
        case 'searching':
            return (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-center px-4" role="status" aria-label="Searching for gyms" aria-busy="true">
                    <Loader2 className="h-10 w-10 text-brand-primary animate-spin mb-4" />
                    <h3 className="text-lg font-semibold text-text-primary mb-2">Finding gyms in {suburb}{useRadius ? ` (${radius}km radius)` : ''}</h3>
                </div>
            );
        // A suburb this platform has no directory for — as opposed to a real, covered
        // suburb whose current filters happen to exclude everything.
        //
        // Audit L7. This case exists because the 'no-results' copy below was actively
        // misleading here: landing on /nsw/fake-suburb-xyz/gyms produced "We couldn't
        // find any martial arts gyms in Fake Suburb Xyz. Try enabling the radius search
        // in filters" plus an Adjust Filters button — presenting an invented place as
        // real and offering an action that cannot possibly help, since no radius or
        // filter change conjures a directory that was never built. The distinguishing
        // signal is whether the build prerendered this route (see the metadata memo in
        // DirectoryPage); the filter CTA is deliberately absent.
        case 'no-directory':
            return (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-center px-4">
                    <h3 className="text-xl font-semibold text-text-primary mb-2">No directory for this suburb</h3>
                    <p className="text-text-muted text-sm mb-3 max-w-sm leading-relaxed">
                        We don't have a martial arts gym directory for <span className="font-medium text-text-primary">{suburb}</span>. It may not be a suburb we cover yet.
                    </p>
                    {/* UI-39: converted to <Button asChild> — same treatment as the two
                        converted CTAs in this file (primary variant supplies the deleted
                        bg/text/hover colours; text-base restores the anchor's inherited
                        16px over the Button base's text-sm; border-0 cancels the variant's
                        1px transparent border so the box size is unchanged). Plain <a>
                        kept deliberately: this branch renders only on non-prerendered
                        routes, so full-page navigation is the correct semantics. Only
                        accepted delta: harmonised white focus ring. */}
                    <Button asChild className="border-0 px-6 py-3 rounded-lg text-base font-semibold">
                        <a href={buildStateHubUrl(routeStateSlug)}>Browse {stateSlugToDisplay(routeStateSlug)} suburbs</a>
                    </Button>
                </div>
            );
        case 'no-results':
            return (
                <div className="col-span-full flex flex-col items-center justify-center py-16 text-center px-4">
                    <h3 className="text-xl font-semibold text-text-primary mb-2">No Gyms Found</h3>
                    <p className="text-text-muted text-sm mb-3 max-w-sm leading-relaxed">
                        We couldn't find any martial arts gyms in <span className="font-medium text-text-primary">{suburb}</span>. {useRadius ? 'Try adjusting your search radius or filters.' : 'Try enabling the radius search in filters.'}
                    </p>
                    {/* UI-13: converted to <Button> — same treatment as the
                        Select Location CTA above. */}
                    <Button
                        onClick={onActionClick}
                        className="border-0 px-6 py-3 rounded-lg text-base font-semibold"
                    >
                        Adjust Filters
                    </Button>
                </div>
            );
        default:
            return null;
    }
};

export default EmptyState;
